import { getTopicProblemSearchTerms } from "./topic-problems";
import { EXPANDED_LIFE_TOPICS } from "./topic-catalog-expansion";

/**
 * OZK managed Topic taxonomy.
 *
 * Topics are intentionally curated at launch. Users can attach questions,
 * experiences, meetings, businesses, and news to these topics, but they should
 * not create arbitrary new topics until the knowledge graph has enough density.
 */

export type TopicTrustLevel = 1 | 2 | 3 | 4 | 5;
export type TopicLocale = "ko" | "en";

export type TopicBusinessCategoryId =
  | "medical"
  | "legal"
  | "accounting"
  | "realestate"
  | "education"
  | "auto"
  | "finance"
  | "service";

export type TopicCommunityMode =
  | "question"
  | "experience"
  | "stage_group"
  | "small_meeting"
  | "expert_answer";

export interface TopicStage {
  id: string;
  label: string;
  labelEn?: string;
  description: string;
  descriptionEn?: string;
}

export interface TopicDefinition {
  id: string;
  label: string;
  labelEn: string;
  category: TopicCategoryId;
  description: string;
  descriptionEn?: string;
  keywords: string[];
  keywordsKo?: string[];
  keywordsEn?: string[];
  seoKeywordsKo?: string[];
  seoKeywordsEn?: string[];
  stages: TopicStage[];
  defaultCommunityModes: TopicCommunityMode[];
  sourceTrust: TopicTrustLevel;
  requiresFreshnessCheck: boolean;
  expertCategories: string[];
  businessCategoryId: TopicBusinessCategoryId;
}

export const TOPIC_CATEGORIES = [
  {
    id: "settlement",
    label: "정착",
    labelEn: "Settlement",
    description: "호주 도착, 행정, 신분증, 기본 생활 준비",
  },
  {
    id: "money",
    label: "세금/돈",
    labelEn: "Tax & Money",
    description: "TFN, ABN, GST, 세금, 은행, 보험, 연금",
  },
  {
    id: "visa",
    label: "비자/이민",
    labelEn: "Visa & Migration",
    description: "학생비자, 워홀, 졸업비자, 영주권, 시민권",
  },
  {
    id: "housing",
    label: "집/부동산",
    labelEn: "Housing & Property",
    description: "렌트, 쉐어, 첫 집 구매, 대출, 투자",
  },
  {
    id: "work",
    label: "일/사업",
    labelEn: "Work & Business",
    description: "구직, 직장, 사업 시작, 고용, Fair Work",
  },
  {
    id: "family",
    label: "가족/교육",
    labelEn: "Family & Education",
    description: "학교, 유학, 육아, 학원, 가족 정착",
  },
  {
    id: "health",
    label: "의료/복지",
    labelEn: "Health & Welfare",
    description: "Medicare, 병원, 치과, 보험, Centrelink",
  },
  {
    id: "local_life",
    label: "지역생활",
    labelEn: "Local Life",
    description: "동네 정보, 교통, 모임, 취미, 생활 편의",
  },
] as const;

export type TopicCategoryId = (typeof TOPIC_CATEGORIES)[number]["id"];

const COMMON_STAGES = {
  researching: {
    id: "researching",
    label: "알아보는 중",
    description: "아직 결정 전이고 기본 정보를 찾는 단계",
  },
  preparing: {
    id: "preparing",
    label: "준비 중",
    description: "서류, 비용, 조건, 일정을 준비하는 단계",
  },
  applying: {
    id: "applying",
    label: "신청/진행 중",
    description: "신청, 계약, 예약, 진행을 실제로 시작한 단계",
  },
  stuck: {
    id: "stuck",
    label: "문제 발생",
    description: "거절, 지연, 분쟁, 오류 등 해결이 필요한 단계",
  },
  solved: {
    id: "solved",
    label: "해결/후기",
    description: "문제가 해결되어 경험과 결과를 공유하는 단계",
  },
} satisfies Record<string, TopicStage>;

export const OZK_TOPICS: TopicDefinition[] = [
  {
    id: "abn",
    label: "ABN",
    labelEn: "Australian Business Number",
    category: "money",
    description: "ABN 신청, 거절, 사업자 세금, 프리랜서/배달/우버 업무 준비",
    keywords: ["abn", "business number", "사업자", "프리랜서", "우버", "sole trader"],
    stages: [
      COMMON_STAGES.researching,
      COMMON_STAGES.preparing,
      COMMON_STAGES.applying,
      COMMON_STAGES.stuck,
      COMMON_STAGES.solved,
    ],
    defaultCommunityModes: ["question", "experience", "expert_answer"],
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["accountant", "tax_agent", "business_advisor"],
    businessCategoryId: "accounting",
  },
  {
    id: "tfn",
    label: "TFN",
    labelEn: "Tax File Number",
    category: "money",
    description: "TFN 신청, 고용주 제출, 세금 신고와 연결되는 기본 세금 번호",
    keywords: ["tfn", "tax file number", "세금번호", "택스파일넘버"],
    stages: [COMMON_STAGES.researching, COMMON_STAGES.applying, COMMON_STAGES.stuck, COMMON_STAGES.solved],
    defaultCommunityModes: ["question", "experience"],
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["accountant", "tax_agent"],
    businessCategoryId: "accounting",
  },
  {
    id: "gst",
    label: "GST",
    labelEn: "Goods and Services Tax",
    category: "money",
    description: "GST 등록, 신고, 기준 매출, 사업 운영 중 세금 의무",
    keywords: ["gst", "bas", "goods and services tax", "세금", "사업"],
    stages: [COMMON_STAGES.researching, COMMON_STAGES.preparing, COMMON_STAGES.applying, COMMON_STAGES.stuck],
    defaultCommunityModes: ["question", "expert_answer"],
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["accountant", "tax_agent"],
    businessCategoryId: "accounting",
  },
  {
    id: "nsw-driver-licence",
    label: "NSW 운전면허",
    labelEn: "NSW Driver Licence",
    category: "settlement",
    description: "한국 면허 전환, Service NSW, 시험, P/L licence, 운전 관련 생활 문제",
    keywords: ["운전면허", "driver licence", "service nsw", "면허전환", "p plate", "l plate"],
    stages: [COMMON_STAGES.researching, COMMON_STAGES.preparing, COMMON_STAGES.applying, COMMON_STAGES.stuck, COMMON_STAGES.solved],
    defaultCommunityModes: ["question", "experience", "small_meeting"],
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["driving_school", "translator"],
    businessCategoryId: "auto",
  },
  {
    id: "medicare",
    label: "Medicare",
    labelEn: "Medicare",
    category: "health",
    description: "Medicare 자격, 신청, 카드, GP/병원 이용과 의료비",
    keywords: ["medicare", "메디케어", "gp", "병원", "의료"],
    stages: [COMMON_STAGES.researching, COMMON_STAGES.applying, COMMON_STAGES.stuck, COMMON_STAGES.solved],
    defaultCommunityModes: ["question", "experience"],
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["gp", "medical_clinic", "insurance_broker"],
    businessCategoryId: "medical",
  },
  {
    id: "centrelink",
    label: "Centrelink",
    labelEn: "Centrelink",
    category: "health",
    description: "Centrelink 신청, 자격, 가족/학생/구직 관련 복지 정보",
    keywords: ["centrelink", "센트링크", "복지", "payment", "mygov"],
    stages: [COMMON_STAGES.researching, COMMON_STAGES.applying, COMMON_STAGES.stuck, COMMON_STAGES.solved],
    defaultCommunityModes: ["question", "experience"],
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["community_service", "migration_agent"],
    businessCategoryId: "service",
  },
  {
    id: "student-visa",
    label: "학생비자",
    labelEn: "Student Visa",
    category: "visa",
    description: "학생비자 신청, 연장, 학업 조건, 졸업 후 경로",
    keywords: ["student visa", "학생비자", "500", "coe", "oshc"],
    stages: [
      { id: "choosing-school", label: "학교 선택", description: "코스와 학교를 고르는 단계" },
      COMMON_STAGES.preparing,
      COMMON_STAGES.applying,
      { id: "studying", label: "재학 중", description: "비자 조건을 지키며 공부하는 단계" },
      { id: "graduate-pathway", label: "졸업 후 준비", description: "졸업비자, 취업, 이민 경로를 보는 단계" },
    ],
    defaultCommunityModes: ["question", "experience", "stage_group", "expert_answer"],
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["education_agent", "migration_agent", "oshc_provider"],
    businessCategoryId: "legal",
  },
  {
    id: "working-holiday",
    label: "워킹홀리데이",
    labelEn: "Working Holiday",
    category: "visa",
    description: "워홀 준비, 입국 초기 정착, 일자리, 농장, 세컨비자, 세금환급",
    keywords: ["워홀", "working holiday", "417", "462", "farm", "second visa"],
    stages: [
      { id: "before-arrival", label: "출국 전", description: "비자, 항공권, 초기 비용을 준비하는 단계" },
      { id: "first-3-months", label: "도착 3개월 이내", description: "집, 일, 계좌, TFN을 해결하는 단계" },
      { id: "job-search", label: "일자리 찾는 중", description: "이력서, 면접, 구직 정보를 찾는 단계" },
      { id: "regional-work", label: "농장/지역 근무", description: "세컨비자 요건과 지역 일자리를 진행하는 단계" },
      COMMON_STAGES.solved,
    ],
    defaultCommunityModes: ["question", "experience", "stage_group", "small_meeting"],
    sourceTrust: 4,
    requiresFreshnessCheck: true,
    expertCategories: ["tax_agent", "recruiter", "migration_agent"],
    businessCategoryId: "legal",
  },
  {
    id: "first-home-buyer",
    label: "첫 집 구매",
    labelEn: "First Home Buyer",
    category: "housing",
    description: "첫 집 구매, 대출, stamp duty, grant, inspection, settlement",
    keywords: ["첫집", "first home buyer", "stamp duty", "fhb", "mortgage", "settlement"],
    stages: [
      { id: "thinking", label: "살까 고민 중", description: "구매 가능성과 예산을 알아보는 단계" },
      { id: "loan-ready", label: "대출 준비", description: "pre-approval, broker, 은행 조건을 보는 단계" },
      { id: "house-hunting", label: "집 찾는 중", description: "suburb, inspection, offer를 진행하는 단계" },
      { id: "contract", label: "계약/정산", description: "계약서, conveyancer, settlement 단계" },
      COMMON_STAGES.solved,
    ],
    defaultCommunityModes: ["question", "experience", "stage_group", "expert_answer", "small_meeting"],
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["mortgage_broker", "conveyancer", "buyer_agent", "real_estate_agent"],
    businessCategoryId: "finance",
  },
  {
    id: "renting",
    label: "렌트",
    labelEn: "Renting",
    category: "housing",
    description: "렌트 신청, bond, inspection, 분쟁, 이사, 쉐어하우스",
    keywords: ["rent", "렌트", "bond", "inspection", "share house", "tenant"],
    stages: [
      { id: "searching", label: "집 찾는 중", description: "지역, 예산, 조건을 찾는 단계" },
      { id: "applying", label: "신청 중", description: "inspection과 application을 진행하는 단계" },
      { id: "living", label: "거주 중", description: "수리, bond, 계약 조건을 관리하는 단계" },
      { id: "moving-out", label: "퇴거/이사", description: "bond 반환, 청소, 분쟁을 처리하는 단계" },
    ],
    defaultCommunityModes: ["question", "experience", "stage_group"],
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["real_estate_agent", "removalist", "cleaner", "tenant_advisor"],
    businessCategoryId: "realestate",
  },
  {
    id: "jobs",
    label: "구직",
    labelEn: "Jobs",
    category: "work",
    description: "호주 구직, 이력서, 면접, 캐주얼/파트타임/전문직 일자리",
    keywords: ["job", "구직", "resume", "interview", "part time", "casual"],
    stages: [COMMON_STAGES.researching, COMMON_STAGES.preparing, { id: "applying", label: "지원 중", description: "이력서 제출과 면접을 진행하는 단계" }, COMMON_STAGES.solved],
    defaultCommunityModes: ["question", "experience", "stage_group", "small_meeting"],
    sourceTrust: 4,
    requiresFreshnessCheck: true,
    expertCategories: ["recruiter", "career_coach", "resume_writer"],
    businessCategoryId: "service",
  },
  {
    id: "school",
    label: "학교/교육",
    labelEn: "School & Education",
    category: "family",
    description: "공립/사립학교, 학군, 입학, 과외, 자녀 교육",
    keywords: [
      "school",
      "school enrolment",
      "school-enrolment",
      "enrolment",
      "학교",
      "학교 등록",
      "입학",
      "학군",
      "tutoring",
      "education",
      "primary",
    ],
    stages: [COMMON_STAGES.researching, COMMON_STAGES.preparing, COMMON_STAGES.applying, { id: "settled", label: "재학/적응", description: "학교 생활과 학습을 이어가는 단계" }],
    defaultCommunityModes: ["question", "experience", "stage_group", "expert_answer"],
    sourceTrust: 4,
    requiresFreshnessCheck: true,
    expertCategories: ["tutor", "education_agent", "school_consultant"],
    businessCategoryId: "education",
  },
  {
    id: "korean-passport",
    label: "한국 여권/재외국민",
    labelEn: "Korean Passport & Overseas Korean",
    category: "settlement",
    description: "한국 여권, 재외국민 등록, 병역, 영사관 업무",
    keywords: ["여권", "재외국민", "영사관", "병역", "passport", "consulate"],
    stages: [COMMON_STAGES.researching, COMMON_STAGES.preparing, COMMON_STAGES.applying, COMMON_STAGES.stuck],
    defaultCommunityModes: ["question", "experience"],
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["legal_advisor", "migration_agent"],
    businessCategoryId: "legal",
  },
  {
    id: "local-meetups",
    label: "지역 모임",
    labelEn: "Local Meetups",
    category: "local_life",
    description: "같은 지역/관심사/생활 단계 사람들이 만나는 소규모 모임",
    keywords: ["모임", "친구", "스터디", "동호회", "meetup"],
    stages: [
      { id: "looking", label: "함께할 사람 찾는 중", description: "같은 관심사와 지역의 사람을 찾는 단계" },
      { id: "first-meeting", label: "첫 모임", description: "소규모 공개 장소 모임을 여는 단계" },
      { id: "recurring", label: "정기 모임", description: "반복 모임 또는 동호회로 성장한 단계" },
    ],
    defaultCommunityModes: ["stage_group", "small_meeting", "experience"],
    sourceTrust: 3,
    requiresFreshnessCheck: false,
    expertCategories: ["community_host", "venue", "coach"],
    businessCategoryId: "service",
  },
  ...EXPANDED_LIFE_TOPICS.map((item): TopicDefinition => ({
    id: item.id,
    label: item.label,
    labelEn: item.labelEn,
    category: item.category,
    description: item.description,
    descriptionEn: item.problem.en,
    keywords: [...item.keywords],
    keywordsKo: [...item.searchQueries.ko],
    keywordsEn: [...item.searchQueries.en],
    stages: [
      COMMON_STAGES.researching,
      COMMON_STAGES.preparing,
      COMMON_STAGES.applying,
      COMMON_STAGES.stuck,
      COMMON_STAGES.solved,
    ],
    defaultCommunityModes: ["question", "experience", "expert_answer"],
    sourceTrust: 4,
    requiresFreshnessCheck: true,
    expertCategories: [item.businessCategoryId],
    businessCategoryId: item.businessCategoryId,
  })),
];

export type TopicId = (typeof OZK_TOPICS)[number]["id"];

export const POPULAR_TOPIC_IDS = [
  "nsw-driver-licence",
  "abn",
  "renting",
  "gst",
  "student-visa",
] as const;

export function getTopicById(id: string): TopicDefinition | undefined {
  return OZK_TOPICS.find((topic) => topic.id === id);
}

export function getTopicsByCategory(category: TopicCategoryId): TopicDefinition[] {
  return OZK_TOPICS.filter((topic) => topic.category === category);
}

export function searchTopics(query: string): TopicDefinition[] {
  const normalize = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, " ")
      .replace(/\s+/g, " ");
  const toTokens = (value: string) =>
    normalize(value)
      .split(" ")
      .filter((token) => token.length >= 2);
  const text = normalize(query);
  if (!text) return OZK_TOPICS;
  const queryTokens = toTokens(text);
  const exactMatches = OZK_TOPICS.filter((topic) =>
    getTopicSearchTerms(topic).some((term) => normalize(term) === text),
  );
  if (exactMatches.length > 0) return exactMatches;

  return OZK_TOPICS.map((topic) => ({
    topic,
    score: getTopicSearchTerms(topic).reduce((total, term) => {
      const normalized = normalize(term);
      if (normalized === text) return total + 20;
      if (normalized.includes(text)) return total + 12;
      if (text.includes(normalized) && normalized.length >= 4) return total + 8;

      const termTokens = toTokens(normalized);
      const overlap = queryTokens.filter((queryToken) =>
        termTokens.some(
          (termToken) =>
            termToken.includes(queryToken) || queryToken.includes(termToken),
        ),
      ).length;
      return total + overlap * 2 + (overlap === queryTokens.length ? 4 : 0);
    }, 0),
  }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.topic.label.localeCompare(b.topic.label))
    .map(({ topic }) => topic);
}

export function getTopicStages(topicId: string): TopicStage[] {
  return getTopicById(topicId)?.stages ?? [];
}

export function resolveTopicsByIds(topicIds: readonly string[]): TopicDefinition[] {
  return topicIds
    .map((topicId) => getTopicById(topicId))
    .filter((topic): topic is TopicDefinition => Boolean(topic));
}

export function getPopularTopics(): TopicDefinition[] {
  return resolveTopicsByIds(POPULAR_TOPIC_IDS);
}

export function isTopicId(id: string): id is TopicId {
  return OZK_TOPICS.some((topic) => topic.id === id);
}

export function getDefaultTopicStage(topicId: string): TopicStage | undefined {
  return getTopicById(topicId)?.stages[0];
}

export function matchTopicsForText(input: string, limit = 5): TopicDefinition[] {
  const text = input.trim().toLowerCase();
  if (!text) return [];

  return OZK_TOPICS.map((topic) => ({
    topic,
    score: getTopicSearchTerms(topic).reduce((total, term) => {
      return total + (text.includes(term.toLowerCase()) ? 2 : 0);
    }, 0),
  }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.topic.label.localeCompare(b.topic.label))
    .slice(0, limit)
    .map(({ topic }) => topic);
}

export function normalizeTopicLocale(value: unknown): TopicLocale {
  if (typeof value !== "string") return "en";
  return value.toLowerCase().startsWith("ko") ? "ko" : "en";
}

export function getTopicLabel(
  topic: TopicDefinition,
  locale: TopicLocale,
): string {
  return locale === "en" ? topic.labelEn || topic.label : topic.label;
}

export function getTopicSecondaryLabel(
  topic: TopicDefinition,
  locale: TopicLocale,
): string | null {
  const secondary = locale === "en" ? topic.label : topic.labelEn;
  return secondary && secondary !== getTopicLabel(topic, locale)
    ? secondary
    : null;
}

export function getTopicDescription(
  topic: TopicDefinition,
  locale: TopicLocale,
): string {
  return locale === "en"
    ? topic.descriptionEn || topic.description
    : topic.description;
}

export function getTopicStageLabel(
  stage: TopicStage,
  locale: TopicLocale,
): string {
  return locale === "en" ? stage.labelEn || stage.label : stage.label;
}

export function getTopicStageDescription(
  stage: TopicStage,
  locale: TopicLocale,
): string {
  return locale === "en"
    ? stage.descriptionEn || stage.description
    : stage.description;
}

export function getTopicCategoryLabel(
  category: (typeof TOPIC_CATEGORIES)[number],
  locale: TopicLocale,
): string {
  return locale === "en" ? category.labelEn : category.label;
}

export function getTopicCategoryDescription(
  category: (typeof TOPIC_CATEGORIES)[number],
  _locale: TopicLocale,
): string {
  return category.description;
}

export function getTopicSearchTerms(topic: TopicDefinition): string[] {
  return uniqueStrings([
    topic.label,
    topic.labelEn,
    topic.id,
    topic.description,
    topic.descriptionEn,
    ...topic.keywords,
    ...(topic.keywordsKo ?? []),
    ...(topic.keywordsEn ?? []),
    ...(topic.seoKeywordsKo ?? []),
    ...(topic.seoKeywordsEn ?? []),
    topic.businessCategoryId,
    ...topic.expertCategories,
    ...topic.stages.flatMap((stage) => [
      stage.label,
      stage.labelEn,
      stage.description,
      stage.descriptionEn,
    ]),
    ...getTopicProblemSearchTerms(topic.id),
  ]);
}

function uniqueStrings(values: Array<string | undefined>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  values.forEach((value) => {
    const normalized = value?.trim();
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(normalized);
  });

  return result;
}
